import React, { useState, useEffect, useRef } from "react";
import { FileText, Download, TrendingUp, Package, Building, DollarSign, Shield } from "lucide-react";
import { Card, PageHeader, Btn, Table, Spinner, ErrBox } from "../components/UI";
import { FilterBar, buildQS, initFilters } from "../components/FilterBar";
import Badge from "../components/Badge";
import { fmtRp, fmtDate } from "../utils/format";
import { reportsAPI, masterAPI } from "../utils/api";

const TABS = [
  { key: "treasury",         label: "Tresuri",         icon: <DollarSign size={14}/> },
  { key: "budget-real",      label: "Anggaran",        icon: <TrendingUp size={14}/> },
  { key: "procurement",      label: "Pengadaan",       icon: <Building size={14}/> },
  { key: "inventory",        label: "Inventori",       icon: <Package size={14}/> },
  { key: "asset",            label: "Aset",            icon: <Building size={14}/> },
  { key: "payment-flow",     label: "Aliran Pembayaran", icon: <DollarSign size={14}/> },
  { key: "audit",            label: "Audit Trail",     icon: <Shield size={14}/> },
];

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState("treasury");
  const [data, setData]   = useState([]);
  const [paymentData, setPaymentData] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr]     = useState("");
  const [filters, setFilters] = useState({ tahun: String(new Date().getFullYear()) });
  const [refUnit, setRefUnit] = useState([]);

  useEffect(() => { loadRefs(); }, []);

  useEffect(() => {
    setLoading(true); setErr("");
    setData([]);
    setPaymentData({});
    const qs = `?tahun=${filters.tahun}`;
    let ignore = false;
    if (activeTab === "treasury") {
      reportsAPI.getTreasury(qs).then(r => {
        if (ignore) return;
        setData(r.data ? [r.data] : []); setLoading(false);
      }).catch(e => { if (!ignore) { setErr(e.message); setLoading(false); } });
    } else if (activeTab === "budget-real") {
      reportsAPI.getBudgetRealization(qs).then(r => {
        if (ignore) return;
        setData(r.data || []); setLoading(false);
      }).catch(e => { if (!ignore) { setErr(e.message); setLoading(false); } });
    } else if (activeTab === "procurement") {
      reportsAPI.getProcurement(qs).then(r => {
        if (ignore) return;
        setData(r.data || []); setLoading(false);
      }).catch(e => { if (!ignore) { setErr(e.message); setLoading(false); } });
    } else if (activeTab === "inventory") {
      reportsAPI.getInventory(qs).then(r => {
        if (ignore) return;
        setData(r.data || []); setLoading(false);
      }).catch(e => { if (!ignore) { setErr(e.message); setLoading(false); } });
    } else if (activeTab === "asset") {
      reportsAPI.getAsset(qs).then(r => {
        if (ignore) return;
        setData(r.data || []); setLoading(false);
      }).catch(e => { if (!ignore) { setErr(e.message); setLoading(false); } });
    } else if (activeTab === "payment-flow") {
      reportsAPI.getPaymentFlow(qs).then(r => {
        if (ignore) return;
        setPaymentData(r.data || {}); setLoading(false);
      }).catch(e => { if (!ignore) { setErr(e.message); setLoading(false); } });
    } else if (activeTab === "audit") {
      reportsAPI.getAuditTrail(qs).then(r => {
        if (ignore) return;
        setData(r.data || []); setLoading(false);
      }).catch(e => { if (!ignore) { setErr(e.message); setLoading(false); } });
    }
    return () => { ignore = true; };
  }, [activeTab, filters.tahun]);

  const loadRefs = async () => {
    try { const u = await masterAPI.getUnit(); setRefUnit((u.data||[]).map(x=>({value:x.id, label:x.nama}))); } catch {}
  };

  const handleExport = (type) => {
    window.open(`${process.env.REACT_APP_API_URL || 'http://103.93.161.255:5200/api'}/reports/export/${type}?tahun=${filters.tahun}`, '_blank');
  };

  const rowStyle = { fontSize:13, color:"#374151" };
  const headStyle = { fontSize:11, fontWeight:700, color:"#64748B", textTransform:"uppercase" };

  return (
    <div>
      <PageHeader title="Laporan & Analisis" subtitle="Laporan keuangan, pengadaan, inventori, dan aset"
        action={
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <Btn variant="secondary" icon={<Download size={14}/>} onClick={() => handleExport('budget')}>Budget</Btn>
            <Btn variant="secondary" icon={<Download size={14}/>} onClick={() => handleExport('revisi')}>Revisi</Btn>
            <Btn variant="secondary" icon={<Download size={14}/>} onClick={() => handleExport('procurement')}>Pengadaan</Btn>
            <Btn variant="secondary" icon={<Download size={14}/>} onClick={() => handleExport('payment-flow')}>Pembayaran</Btn>
            <Btn variant="secondary" icon={<Download size={14}/>} onClick={() => handleExport('treasury')}>Tresuri</Btn>
            <Btn variant="secondary" icon={<Download size={14}/>} onClick={() => handleExport('audit-history')}>Audit History</Btn>
          </div>
        }
      />
      <FilterBar filters={filters} onChange={setFilters}>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 14px",
                borderRadius:20, border:"1.5px solid", cursor:"pointer", fontSize:12,
                fontFamily:"inherit",
                background: activeTab === t.key ? "#2563EB" : "white",
                borderColor: activeTab === t.key ? "#2563EB" : "#E2E8F0",
                color: activeTab === t.key ? "white" : "#64748B",
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </FilterBar>

      {err && <div style={{marginBottom:12}}><ErrBox msg={err} onRetry={load}/></div>}

      {loading ? <Spinner/> : (
        <Card>
          {activeTab === "treasury" && (data && data[0] ? (
            <div>
              <h3 style={{margin:"0 0 16px", fontSize:14, fontWeight:700}}>Posisi Kas per Rekening - {filters.tahun}</h3>
              <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20}}>
                {[
                  ["Total Saldo", data[0].total_saldo, "#2563EB"],
                  ["Total Masuk", data[0].total_masuk, "#059669"],
                  ["Total Keluar", data[0].total_keluar, "#DC2626"],
                ].map(([l,v,c]) => (
                  <div key={l} style={{padding:"12px 16px", border:"1px solid #E2E8F0", borderRadius:8}}>
                    <p style={{margin:"0 0 4px", fontSize:11, color:"#6B7280", textTransform:"uppercase"}}>{l}</p>
                    <p style={{margin:0, fontSize:16, fontWeight:700, color:c, fontFamily:"monospace"}}>{fmtRp(v)}</p>
                  </div>
                ))}
              </div>
              <Table pageSize={10} cols={[
                {key:"nama_bank", label:"Bank", render:v=><b style={{fontSize:13}}>{v}</b>},
                {key:"no_rekening", label:"No. Rekening", fontSize:13, color:"#374151"},
                {key:"saldo_awal", label:"Saldo Awal", align:"right", render:v=><span style={{fontFamily:"monospace",fontSize:12}}>{fmtRp(Number(v))}</span>},
                {key:"total_masuk", label:"Masuk", align:"right", render:v=><span style={{fontFamily:"monospace",fontSize:12,color:"#059669"}}>{fmtRp(Number(v))}</span>},
                {key:"total_keluar", label:"Keluar", align:"right", render:v=><span style={{fontFamily:"monospace",fontSize:12,color:"#DC2626"}}>{fmtRp(Number(v))}</span>},
                {key:"saldo_akhir", label:"Saldo Akhir", align:"right", render:v=><span style={{fontFamily:"monospace",fontSize:12,fontWeight:700}}>{fmtRp(Number(v))}</span>},
              ]} data={data[0].rekening||[]}/>
            </div>
          ) : (
            <div style={{padding:"20px",textAlign:"center",color:"#9CA3AF"}}>Tidak ada data tresuri</div>
          ))}

          {activeTab === "budget-real" && (
            <div>
              <h3 style={{margin:"0 0 16px", fontSize:14, fontWeight:700}}>Anggaran vs Realisasi - {filters.tahun}</h3>
              <Table pageSize={10} cols={[
                {key:"unit", label:"Unit"},
                {key:"kode_akun", label:"Kode Akun", nowrap:true},
                {key:"nama_akun", label:"Nama Akun"},
                {key:"pagu", label:"Pagu", align:"right", render:v=><span style={{fontFamily:"monospace",fontSize:12}}>{fmtRp(Number(v))}</span>},
                {key:"total_pengeluaran", label:"Realisasi", align:"right", render:v=><span style={{fontFamily:"monospace",fontSize:12,color:"#DC2626"}}>{fmtRp(Number(v))}</span>},
                {key:"sisa", label:"Sisa", align:"right", render:v=><span style={{fontFamily:"monospace",fontSize:12,color:"#059669"}}>{fmtRp(Number(v))}</span>},
                {key:"percent", label:"%", align:"right", render:(v) => { const c = Number(v)>80?"#DC2626":Number(v)>60?"#F59E0B":"#2563EB"; return <span style={{fontWeight:700,color:c}}>{v}%</span>; }},
              ]} data={data}/>
            </div>
          )}

          {activeTab === "procurement" && (
            <div>
              <h3 style={{margin:"0 0 16px", fontSize:14, fontWeight:700}}>Laporan Pengadaan - {filters.tahun}</h3>
              <Table pageSize={10} cols={[
                {key:"no_pr", label:"No. PR", nowrap:true},
                {key:"tanggal", label:"Tanggal", render:v=>fmtDate(v), nowrap:true},
                {key:"jenis", label:"Jenis", align:"center", render:v=>v==='ASSET'?(
                  <span style={{padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600,background:"#E9D5FF",color:"#581C87"}}>ASSET</span>
                ):(
                  <span style={{padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600,background:"#DBEAFE",color:"#1E40AF"}}>INVENTORY</span>
                )},
                {key:"unit", label:"Unit"},
                {key:"vendor", label:"Vendor", render:v=>v||'-'},
                {key:"jumlah_item", label:"Items", align:"center"},
                {key:"asset_count", label:"Asset Dibuat", align:"center", render:v=><span style={{fontFamily:"monospace",fontWeight:600,color:"#581C87"}}>{v||0}</span>},
                {key:"receipt_count", label:"Receipt", align:"center", render:v=><span style={{fontFamily:"monospace",fontWeight:600,color:"#1E40AF"}}>{v||0}</span>},
                {key:"nilai", label:"Nilai", align:"right", render:v=><span style={{fontFamily:"monospace",fontWeight:600,color:"#1E40AF"}}>{fmtRp(Number(v)||0)}</span>},
                {key:"status", label:"Status", render:v=><Badge status={v}/>},
              ]} data={data}/>
            </div>
          )}

          {activeTab === "inventory" && (
            <div>
              <h3 style={{margin:"0 0 16px", fontSize:14, fontWeight:700}}>Inventori - {filters.tahun}</h3>
              <Table pageSize={10} cols={[
                {key:"warehouse_nama", label:"Gudang"},
                {key:"item_kode", label:"Kode Item", nowrap:true},
                {key:"item_nama", label:"Nama Item"},
                {key:"satuan", label:"Satuan"},
                {key:"qty_on_hand", label:"Stok", align:"right", render:v=><span style={{fontFamily:"monospace",fontWeight:600}}>{v}</span>},
                {key:"min_stok", label:"Min", align:"right"},
                {key:"batch_number", label:"Batch"},
                {key:"expire_date", label:"Exp Date", render:v=>v?fmtDate(v):"-"},
                {key:"stock_status", label:"Status", render:v=>{
                  const cfg = {LOW:{color:"#DC2626",bg:"#FEE2E2"},EXPIRED:{color:"#7C3AED",bg:"#EDE9FE"},OK:{color:"#059669",bg:"#D1FAE5"}};
                  const c = cfg[v] || cfg.OK;
                  return <span style={{padding:"3px 10px", borderRadius:12, fontSize:11, fontWeight:600, background:c.bg, color:c.color}}>{v}</span>;
                }},
              ]} data={data}/>
            </div>
          )}

          {activeTab === "asset" && (
            <div>
              <h3 style={{margin:"0 0 16px", fontSize:14, fontWeight:700}}>Laporan Aset</h3>
              <Table pageSize={10} cols={[
                {key:"code", label:"Kode Aset", nowrap:true},
                {key:"name", label:"Nama Aset"},
                {key:"category", label:"Kategori"},
                {key:"acquisition_value", label:"Nilai Perolehan", align:"right", render:v=><span style={{fontFamily:"monospace"}}>{fmtRp(Number(v))}</span>},
                {key:"total_depreciation", label:"Penyusutan", align:"right", render:v=><span style={{fontFamily:"monospace",color:"#DC2626"}}>{fmtRp(Number(v))}</span>},
                {key:"book_value", label:"Nilai Buku", align:"right", render:v=><span style={{fontFamily:"monospace",fontWeight:700}}>{fmtRp(Number(v))}</span>},
                {key:"condition", label:"Kondisi", render:v=><Badge status={v}/>},
                {key:"location", label:"Lokasi"},
              ]} data={data}/>
            </div>
          )}

          {activeTab === "payment-flow" && paymentData.status_breakdown && (
            <div>
              <h3 style={{margin:"0 0 16px", fontSize:14, fontWeight:700}}>Aliran Pembayaran - {filters.tahun}</h3>
              <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20}}>
                {(paymentData.status_breakdown||[]).map(s => (
                  <div key={s.status} style={{padding:"12px 16px", border:"1px solid #E2E8F0", borderRadius:8}}>
                    <p style={{margin:"0 0 4px", fontSize:11, color:"#6B7280", textTransform:"uppercase"}}>{s.status}</p>
                    <p style={{margin:"0 0 2px", fontSize:20, fontWeight:700, color:"#2563EB"}}>{s.count} SPP</p>
                    <p style={{margin:0, fontSize:12, color:"#64748B"}}>{fmtRp(Number(s.nilai))}</p>
                  </div>
                ))}
              </div>
              {(paymentData.vendor_summary||[]).length > 0 && (
                <>
                  <h4 style={{margin:"0 0 10px", fontSize:13, fontWeight:700}}>Top Vendor by Nilai</h4>
                  <Table pageSize={10} cols={[
                    {key:"vendor", label:"Vendor"},
                    {key:"total_pr", label:"Jumlah PR", align:"right"},
                    {key:"total_nilai", label:"Total Nilai", align:"right", render:v=><span style={{fontFamily:"monospace",fontWeight:600,color:"#2563EB"}}>{fmtRp(Number(v))}</span>},
                  ]} data={paymentData.vendor_summary}/>
                </>
              )}
            </div>
          )}

          {activeTab === "audit" && (
            <div>
              <h3 style={{margin:"0 0 16px", fontSize:14, fontWeight:700}}>Audit Trail</h3>
              <Table pageSize={10} cols={[
                {key:"created_at", label:"Waktu", render:v=>fmtDate(v), nowrap:true},
                {key:"user_name", label:"User"},
                {key:"action", label:"Aksi", render:v=><span style={{fontFamily:"monospace",fontSize:12}}>{v}</span>},
                {key:"table_name", label:"Tabel"},
                {key:"record_id", label:"Record ID", render:v=>v?(typeof v==='string'?v.slice(0,8):v):"-"},
                {key:"changes", label:"Perubahan", render:v=>v?JSON.stringify(v).slice(0,60):"-"},
              ]} data={(data.rows||data)||[]}/>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;